/* oxlint-disable max-lines */
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { PROFILE_TYPES, RECORD_TYPE } from '@/app/data/model_constants';
import { VITE_ENV_DEVELOPMENT } from '@/app/data/general_constants';

export default function useActivityNotificationOpen() {
  const store = useStore();
  const notifiables_is_unopened = ref({});

  // Vuex Getters
  const activities = computed(() => store.getters.activities);
  const current_is_demo = computed(() => store.getters.current_is_demo);
  const current_is_impersonation = computed(() => store.getters.current_is_impersonation);
  const current_profile_true = computed(() => store.getters.current_profile_true);
  const current_client = computed(() => store.getters.current_client);
  const notifications = computed(() => store.getters.notifications);
  const current_profile = computed(() => store.getters.current_profile);

  const can_open_implicitly = computed(
    () => !current_is_impersonation.value || current_is_demo.value || import.meta.env.VITE_ENV === VITE_ENV_DEVELOPMENT,
  );

  // Computed
  function is_notifiable_unopened(notifiable) {
    return !!notifiables_is_unopened.value[`${notifiable.type}_${notifiable.id}`];
  }

  // --- Filtering ---
  function filterNotifications(notifiables, target) {
    let notificationsArr = [];
    if (
      notifiables &&
      ('id' in notifiables || (Array.isArray(notifiables) && notifiables.length > 0) || !_.isEmpty(notifiables))
    ) {
      let notifiable_ids;
      let notifiable_types;
      if ('id' in notifiables) {
        notifiable_ids = { [notifiables.type]: notifiables.id };
        notifiable_types = [notifiables.type];
      } else {
        notifiable_ids = {};
        _.each(notifiables, (notifiable) => {
          if (!(notifiable.type in notifiable_ids)) notifiable_ids[notifiable.type] = [];
          notifiable_ids[notifiable.type].push(notifiable.id);
        });
        notifiable_types = _.keys(notifiable_ids);
      }
      notificationsArr = _.filter(
        target.type === RECORD_TYPE.UNIT ? activities.value : notifications.value,
        (notification) =>
          notifiable_types.includes(notification.attributes.notifiable_type) &&
          notifiable_ids[notification.attributes.notifiable_type].includes(notification.attributes.notifiable_id) &&
          notification.attributes.target_type === target.type &&
          (target.id === null || notification.attributes.target_id === target.id) &&
          (notification.attributes.opened_at === null || notification.attributes.read_at === null),
      );
    }
    return notificationsArr;
  }

  // --- Notification Openers (Explicit) ---
  async function openAnnouncementNotifications(notifiables) {
    const filtered = filterNotifications(notifiables, current_profile.value);
    const notifiable_type =
      current_profile.value.type === PROFILE_TYPES.CUSTOMER ? RECORD_TYPE.ANNOUNCEMENT_LEVEL : RECORD_TYPE.ANNOUNCEMENT;
    const ids = _.chain(filtered)
      .filter(['attributes.notifiable_type', notifiable_type])
      .map('attributes.notifiable_id')
      .value();
    if (ids.length === 0) return;
    await store.dispatch('OPEN_ANNOUNCEMENT_NOTIFICATIONS', { ids });
  }

  async function openDocumentNotifications(notifiables) {
    const filtered = filterNotifications(notifiables, current_profile.value);
    const ids = _.chain(filtered)
      .filter(['attributes.notifiable_type', RECORD_TYPE.DOCUMENT])
      .map('attributes.notifiable_id')
      .value();
    if (ids.length === 0) return;
    await store.dispatch('OPEN_DOCUMENT_NOTIFICATIONS', { ids });
  }

  async function openExportNotifications(notifiables) {
    const filtered = filterNotifications(notifiables, current_profile.value);
    const ids = _.chain(filtered)
      .filter(['attributes.notifiable_type', RECORD_TYPE.EXPORT])
      .map('attributes.notifiable_id')
      .value();
    if (ids.length === 0) return;
    await store.dispatch('OPEN_EXPORT_NOTIFICATIONS', { ids });
  }

  async function openInstallmentNotifications(notifiables) {
    const filtered = filterNotifications(notifiables, current_profile.value);
    const ids = _.chain(filtered)
      .filter(['attributes.notifiable_type', RECORD_TYPE.INSTALLMENT])
      .map('attributes.notifiable_id')
      .value();
    if (ids.length === 0) return;
    await store.dispatch('OPEN_INSTALLMENT_NOTIFICATIONS', { ids });
  }

  async function openIssueNotifications(notifiables) {
    const filtered = filterNotifications(notifiables, current_profile.value);
    const ids = _.chain(filtered)
      .filter(['attributes.notifiable_type', RECORD_TYPE.ISSUE])
      .map('attributes.notifiable_id')
      .value();
    if (ids.length === 0) return;
    await store.dispatch('OPEN_ISSUE_NOTIFICATIONS', { ids });
  }

  async function openIssueListNotifications(issue_lists) {
    const filtered = filterNotifications(issue_lists, current_profile.value);
    const ids = _.chain(filtered)
      .filter(['attributes.notifiable_type', RECORD_TYPE.ISSUE_LIST])
      .map('attributes.notifiable_id')
      .value();
    if (ids.length === 0) return;
    await store.dispatch('OPEN_ISSUE_LIST_NOTIFICATIONS', { ids: _.map(issue_lists, 'id') });
  }

  async function openQuestionNotifications(questions) {
    const filtered = filterNotifications(questions, current_profile.value);
    const ids = _.chain(filtered)
      .filter(['attributes.notifiable_type', RECORD_TYPE.QUESTION])
      .map('attributes.notifiable_id')
      .value();
    if (ids.length === 0) return;
    await store.dispatch('OPEN_QUESTION_NOTIFICATIONS', { ids });
  }

  async function openSurveyNotifications(notifiables) {
    const filtered = filterNotifications(notifiables, current_profile.value);
    const ids = _.chain(filtered)
      .filter(['attributes.notifiable_type', RECORD_TYPE.SURVEY])
      .map('attributes.notifiable_id')
      .value();
    if (ids.length === 0) return;
    await store.dispatch('OPEN_SURVEY_NOTIFICATIONS', { ids });
  }

  async function openTicketNotifications(notifiables, ticket_id) {
    const filtered = filterNotifications(notifiables, current_profile.value);
    if (filtered.length === 0) return;
    await store.dispatch('OPEN_TICKET_NOTIFICATIONS', { ids: [ticket_id] });
  }

  async function openEmailConversationNotifications(notifiables, email_conversation_id) {
    const filtered = filterNotifications(notifiables, current_profile.value);
    if (filtered.length === 0) return;
    await store.dispatch('OPEN_EMAIL_CONVERSATION_NOTIFICATIONS', { ids: [email_conversation_id] });
  }

  async function openTodoNotifications(notifiables) {
    const filtered = filterNotifications(notifiables, current_profile.value);
    const ids = _.chain(filtered)
      .filter(['attributes.notifiable_type', RECORD_TYPE.TODO])
      .map('attributes.notifiable_id')
      .value();
    if (ids.length === 0) return;
    await store.dispatch('OPEN_TODO_NOTIFICATIONS', { ids });
  }

  async function openDecisionNotifications(notifiables, decision_id) {
    const filtered = filterNotifications(notifiables, current_profile.value);
    if (filtered.length === 0) return;
    await store.dispatch('OPEN_DECISION_NOTIFICATIONS', { ids: [decision_id] });
  }

  async function openDecisionProposalNotifications(notifiables, decision_id) {
    const filtered = filterNotifications(notifiables, current_profile.value);
    if (filtered.length === 0) return;
    await store.dispatch('OPEN_DECISION_PROPOSAL_NOTIFICATIONS', { ids: [decision_id] });
  }

  function filterActivities(notifiables) {
    return filterNotifications(notifiables, { type: RECORD_TYPE.UNIT, id: null });
  }

  // --- Activities Openers (Explicit) ---
  async function openAnnouncementActivities(notifiables) {
    const activitiesArr = filterActivities(notifiables);
    const announcement_ids = _.chain(activitiesArr)
      .filter(['attributes.notifiable_type', RECORD_TYPE.ANNOUNCEMENT_LEVEL])
      .map('attributes.parameters.announcement_id')
      .value();
    if (announcement_ids.length === 0) return;
    await store.dispatch('OPEN_ANNOUNCEMENT_ACTIVITIES', { ids: [announcement_ids] });
  }
  async function openDecisionActivities(notifiables) {
    const activitiesArr = filterActivities(notifiables);
    const decision_ids = _.map(activitiesArr, 'attributes.parameters.decision_id');
    if (decision_ids.length === 0) return;
    await store.dispatch('OPEN_DECISION_ACTIVITIES', { ids: decision_ids });
  }
  async function openDecisionProposalActivities(notifiables) {
    const activitiesArr = filterActivities(notifiables);
    const decision_proposal_ids = _.chain(activitiesArr)
      .filter(['attributes.notifiable_type', RECORD_TYPE.DECISION_PROPOSAL])
      .map('attributes.notifiable_id')
      .value();
    if (decision_proposal_ids.length === 0) return;
    await store.dispatch('OPEN_DECISION_PROPOSAL_ACTIVITIES', { ids: decision_proposal_ids });
  }
  async function openDocumentActivities(notifiables) {
    const activitiesArr = filterActivities(notifiables);
    const document_ids = _.chain(activitiesArr)
      .filter(['attributes.notifiable_type', RECORD_TYPE.DOCUMENT])
      .map('attributes.notifiable_id')
      .value();
    if (document_ids.length === 0) return;
    await store.dispatch('OPEN_DOCUMENT_ACTIVITIES', { ids: document_ids });
  }
  async function openQuestionActivities(notifiables) {
    const activitiesArr = filterActivities(notifiables);
    const question_ids = _.chain(activitiesArr)
      .filter(['attributes.notifiable_type', RECORD_TYPE.QUESTION])
      .map('attributes.notifiable_id')
      .value();
    if (question_ids.length === 0) return;
    await store.dispatch('OPEN_QUESTION_ACTIVITIES', { ids: question_ids });
  }
  async function openSurveyActivities(notifiables) {
    const activitiesArr = filterActivities(notifiables);
    const survey_ids = _.chain(activitiesArr)
      .filter(['attributes.notifiable_type', RECORD_TYPE.SURVEY])
      .map('attributes.notifiable_id')
      .value();
    if (survey_ids.length === 0) return;
    await store.dispatch('OPEN_SURVEY_ACTIVITIES', { ids: survey_ids });
  }
  async function openTicketActivities(notifiables, ticket_id) {
    const activitiesArr = filterActivities(notifiables);
    if (activitiesArr !== null && activitiesArr.length > 0) {
      await store.dispatch('OPEN_TICKET_ACTIVITIES', { ids: [ticket_id] });
    }
  }

  // --- Tracking ---
  function trackNotifiablesUnopened(notifiables) {
    if (
      notifiables &&
      ('id' in notifiables || (Array.isArray(notifiables) && notifiables.length > 0) || !_.isEmpty(notifiables))
    ) {
      let notifiableIds;
      let type;
      if ('id' in notifiables) {
        notifiableIds = [notifiables.id];
        type = notifiables.type;
      } else if (Array.isArray(notifiables)) {
        notifiableIds = _.map(notifiables, 'id');
        type = notifiables[0].type;
      } else {
        notifiableIds = Object.keys(notifiables);
        type = notifiables[notifiableIds[0]].type;
      }
      const notifiableNotifications = _.filter(
        notifications.value,
        (notification) =>
          notifiableIds.includes(notification.attributes.notifiable_id) &&
          notification.attributes.notifiable_type === type &&
          notification.attributes.target_type === current_profile.value.type &&
          notification.attributes.target_id === current_profile.value.id &&
          (notification.attributes.opened_at === null || notification.attributes.read_at === null),
      );
      notifiableNotifications.forEach((notification) => {
        const { notifiable_id, notifiable_type } = notification.attributes;
        notifiables_is_unopened.value[`${notifiable_type}_${notifiable_id}`] = true;
      });
    }
  }

  // --- Notification Openers (Implicit) ---
  async function openAnnouncementNotificationsImplicit(notifiables) {
    if (!can_open_implicitly.value) return;
    await openAnnouncementNotifications(notifiables);
  }
  async function openDocumentNotificationsImplicit(notifiables) {
    if (!can_open_implicitly.value) return;
    await openDocumentNotifications(notifiables);
  }
  async function openExportNotificationsImplicit(notifiables) {
    if (!can_open_implicitly.value) return;
    await openExportNotifications(notifiables);
  }
  async function openInstallmentNotificationsImplicit(notifiables) {
    if (!can_open_implicitly.value) return;
    await openInstallmentNotifications(notifiables);
  }
  async function openIssueNotificationsImplicit(notifiables) {
    if (!can_open_implicitly.value) return;
    await openIssueNotifications(notifiables);
  }
  async function openIssueListNotificationsImplicit(issue_lists) {
    if (!can_open_implicitly.value) return;
    await openIssueListNotifications(issue_lists);
  }
  async function openQuestionNotificationsImplicit(notifiables) {
    if (!can_open_implicitly.value) return;
    await openQuestionNotifications(notifiables);
  }
  async function openSurveyNotificationsImplicit(notifiables) {
    if (!can_open_implicitly.value) return;
    await openSurveyNotifications(notifiables);
  }
  async function openSurveyNotificationsExplicit(notifiables) {
    await openSurveyNotifications(notifiables);
  }
  async function openTicketNotificationsImplicit(notifiables, ticket_id) {
    if (!can_open_implicitly.value) return;
    await openTicketNotifications(notifiables, ticket_id);
  }
  async function openEmailConversationNotificationsImplicit(notifiables, email_conversation_id) {
    if (!can_open_implicitly.value) return;
    await openEmailConversationNotifications(notifiables, email_conversation_id);
  }
  async function openTodoNotificationsImplicit(notifiables) {
    if (!can_open_implicitly.value) return;
    await openTodoNotifications(notifiables);
  }
  async function openDecisionNotificationsImplicit(notifiables, decision_id) {
    if (!can_open_implicitly.value) return;
    await openDecisionNotifications(notifiables, decision_id);
  }
  async function openDecisionProposalNotificationsImplicit(notifiables, decision_id) {
    if (!can_open_implicitly.value) return;
    await openDecisionProposalNotifications(notifiables, decision_id);
  }

  // --- Activities Openers (Implicit) ---
  async function openAnnouncementActivitiesImplicit(notifiables) {
    if (!can_open_implicitly.value) return;
    await openAnnouncementActivities(notifiables);
  }
  async function openDecisionActivitiesImplicit(notifiables) {
    if (!can_open_implicitly.value) return;
    await openDecisionActivities(notifiables);
  }
  async function openDecisionProposalActivitiesImplicit(notifiables) {
    if (!can_open_implicitly.value) return;
    await openDecisionProposalActivities(notifiables);
  }
  async function openDocumentActivitiesImplicit(notifiables) {
    if (!can_open_implicitly.value) return;
    await openDocumentActivities(notifiables);
  }
  async function openQuestionActivitiesImplicit(notifiables) {
    if (!can_open_implicitly.value) return;
    await openQuestionActivities(notifiables);
  }
  async function openSurveyActivitiesImplicit(notifiables) {
    if (!can_open_implicitly.value) return;
    await openSurveyActivities(notifiables);
  }
  async function openTicketActivitiesImplicit(notifiables, ticket_id) {
    if (!can_open_implicitly.value) return;
    await openTicketActivities(notifiables, ticket_id);
  }

  // Return everything needed
  return {
    // State
    notifiables_is_unopened,

    // Computed
    is_notifiable_unopened,

    openAnnouncementNotificationsImplicit,
    openDocumentNotificationsImplicit,
    openExportNotificationsImplicit,
    openInstallmentNotificationsImplicit,
    openIssueNotificationsImplicit,
    openIssueListNotificationsImplicit,
    openQuestionNotificationsImplicit,
    openSurveyNotificationsImplicit,
    openSurveyNotificationsExplicit,
    openTicketNotificationsImplicit,
    openEmailConversationNotificationsImplicit,
    openTodoNotificationsImplicit,
    openDecisionNotificationsImplicit,
    openDecisionProposalNotificationsImplicit,

    openAnnouncementNotifications,
    openDocumentNotifications,
    openExportNotifications,
    openInstallmentNotifications,
    openIssueNotifications,
    openIssueListNotifications,
    openQuestionNotifications,
    openSurveyNotifications,
    openTicketNotifications,
    openEmailConversationNotifications,
    openTodoNotifications,
    openDecisionNotifications,
    openDecisionProposalNotifications,

    // Activities openers
    openAnnouncementActivitiesImplicit,
    openDecisionActivitiesImplicit,
    openDecisionProposalActivitiesImplicit,
    openDocumentActivitiesImplicit,
    openQuestionActivitiesImplicit,
    openSurveyActivitiesImplicit,
    openTicketActivitiesImplicit,

    openAnnouncementActivities,
    openDecisionActivities,
    openDecisionProposalActivities,
    openDocumentActivities,
    openQuestionActivities,
    openSurveyActivities,
    openTicketActivities,

    // Filtering
    filterNotifications,
    filterActivities,

    // Tracking
    trackNotifiablesUnopened,

    // Vuex state if needed
    activities,
    current_is_demo,
    current_is_impersonation,
    current_profile_true,
    current_client,
    notifications,
    current_profile,
  };
}
